// src/components/Header.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Typeahead, Menu, MenuItem } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";

import { MdHistory } from "react-icons/md";
import { BsSearch } from "react-icons/bs";
import { IoArrowBack } from "react-icons/io5";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiShoppingCart } from "react-icons/fi";
import { GoHome } from "react-icons/go";
import { TbCategory2 } from "react-icons/tb";
import { IoHeartOutline, IoPersonOutline } from "react-icons/io5";
import { HiOutlineShoppingCart } from "react-icons/hi2";

import kuptoLogo from "../images/logo/kupto-final logo-2.png";
import userIcon from "../images/icon/user.png";
import heartIcon from "../images/icon/heart.png";
import cartIcon from "../images/icon/cart.png";
import menuIcon from "../images/icon/category-01.png";
import "../pages/styles/header.css";
import { pushSearchHistory } from "../utils/searchHistory";

import {
  getAllProducts,
  selectUniqueCategories,
} from "../app/features/products/productSlice";
import {
  fetchCategories,
  selectCategories,
} from "../app/features/category/categorySlice";
import { getUserCart } from "../app/features/user/userSlice";

const RECENT_KEY = "kupto_recent_searches";

/* =========================
   ✅ GUEST CART (localStorage)
========================= */
const GUEST_CART_KEY = "kupto_guest_cart_v1";

const readGuestCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

/* =========================
   ✅ AUTH HELPERS
========================= */
const getStoredAuthToken = () => {
  try {
    const directToken = localStorage.getItem("token");
    if (directToken && directToken !== "undefined" && directToken !== "null") {
      return directToken;
    }

    const customerRaw = localStorage.getItem("customer");
    if (customerRaw) {
      const customer = JSON.parse(customerRaw);
      const customerToken = customer?.token || customer?.accessToken;
      if (
        customerToken &&
        customerToken !== "undefined" &&
        customerToken !== "null"
      ) {
        return customerToken;
      }
    }

    const userRaw = localStorage.getItem("user");
    if (userRaw) {
      const user = JSON.parse(userRaw);
      const userToken = user?.token || user?.accessToken;
      if (userToken && userToken !== "undefined" && userToken !== "null") {
        return userToken;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const getStoredUser = () => {
  try {
    const customerRaw = localStorage.getItem("customer");
    if (customerRaw) {
      const customer = JSON.parse(customerRaw);
      if (customer && typeof customer === "object") return customer;
    }

    const userRaw = localStorage.getItem("user");
    if (userRaw) {
      const user = JSON.parse(userRaw);
      if (user && typeof user === "object") return user;
    }

    return null;
  } catch {
    return null;
  }
};

// helper near other helpers
const getProductRouteId = (p) =>
  String(p?.slug || p?._id || p?.id || "").trim();

const buildSearchSuggestions = ({
  searchValue,
  recentSearches = [],
  productList = [],
  historyLimit = 12,
  productLimit = 20,
  historyKeyPrefix = "h",
  productKeyPrefix = "p",
  includeProductsWhenEmpty = false,
}) => {
  const q = (searchValue || "").trim().toLowerCase();

  const history = recentSearches
    .filter((s) => (q ? s.toLowerCase().includes(q) : true))
    .slice(0, historyLimit)
    .map((s) => ({
      type: "history",
      label: s,
      name: s,
      key: `${historyKeyPrefix}-${s}`,
      id: `${historyKeyPrefix}-${s}`,
    }));

  const products = productList
    .filter((p) => {
      if (!q && !includeProductsWhenEmpty) return false;
      const title = (p?.title || "").toLowerCase();
      return q ? title.includes(q) : true;
    })
    .slice(0, productLimit)
    .map((p) => {
      const routeId = getProductRouteId(p);

      return {
        type: "product",
        label: p?.title || "Untitled",
        name: p?.title || "Untitled",
        key: `${productKeyPrefix}-${routeId || p?._id || p?.id}`,
        id: routeId || p?._id || p?.id,
        routeId,
        prod: p,
      };
    });

  if (q) return [...products, ...history];
  return history.length ? history : products;
};

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === "/";
  const isStorePage = useMemo(
    () => location.pathname === "/product",
    [location.pathname]
  );

  // ✅ only show the APP single-product header + overlay on /product/:idOrSlug
  const isSingleProductPage = useMemo(() => {
    const p = location.pathname || "";
    return /^\/product\/[^/]+$/.test(p);
  }, [location.pathname]);

  // ✅ where to return after auth
  const returnTo = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search]
  );

  // ===== Redux state =====
  const authState = useSelector((state) => state?.auth);
  const cartState = useSelector((state) => state?.auth?.cartProduct);

  // ✅ IMPORTANT: pull products from slice (service already normalizes)
  const rawProducts = useSelector((state) => state?.product?.products);

  const categoryState = useSelector((state) => state?.category || {});
  const categories = useSelector(selectCategories);
  const categoriesLoading = Boolean(categoryState?.isLoading);

  const uniqueCategories = useSelector((state) =>
    selectUniqueCategories(state, categories ?? [])
  );

  /* =========================
     ✅ Stored user sync
  ========================== */
  const [storedUser, setStoredUser] = useState(() => getStoredUser());

  useEffect(() => {
    const syncStoredUser = (e) => {
      if (!e || !e.key || ["customer", "user", "token"].includes(e.key)) {
        setStoredUser(getStoredUser());
      }
    };

    const onAuthUpdated = () => setStoredUser(getStoredUser());

    window.addEventListener("storage", syncStoredUser);
    window.addEventListener("authUpdated", onAuthUpdated);

    return () => {
      window.removeEventListener("storage", syncStoredUser);
      window.removeEventListener("authUpdated", onAuthUpdated);
    };
  }, []);

  const currentUser = authState?.user || storedUser || null;

  // ✅ login check (redux or localStorage)
  const isLoggedIn = Boolean(
    currentUser?.token || currentUser?.accessToken || getStoredAuthToken()
  );

  // ✅ Normalize products into array
  const productList = useMemo(() => {
    if (Array.isArray(rawProducts)) return rawProducts;
    if (rawProducts && Array.isArray(rawProducts.products))
      return rawProducts.products;
    return [];
  }, [rawProducts]);

  // ✅ Normalize backend cart into array (prevents reduce crash)
  const backendCartList = useMemo(() => {
    if (Array.isArray(cartState)) return cartState;
    if (cartState && Array.isArray(cartState?.cart)) return cartState.cart;
    return [];
  }, [cartState]);

  /* =========================
     ✅ Guest cart state + sync
  ========================== */
  const [guestCart, setGuestCart] = useState(() => readGuestCart());

  useEffect(() => {
    const sync = () => setGuestCart(readGuestCart());

    const onStorage = (e) => {
      if (!e) return;
      if (e.key === GUEST_CART_KEY) sync();
    };

    const onGuestEvent = () => sync();

    window.addEventListener("storage", onStorage);
    window.addEventListener("guestCartUpdated", onGuestEvent);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("guestCartUpdated", onGuestEvent);
    };
  }, []);

  // ===== UI state =====
  const [isScrolled, setIsScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const [searchTermState, setSearchTermState] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // ✅ HOME: trigger bar + FULL overlay (blank page like single product)
  const [homeOverlayOpen, setHomeOverlayOpen] = useState(false);
  const [homeOverlaySearch, setHomeOverlaySearch] = useState("");
  const homeOverlayInputRef = useRef(null);

  // ✅ STORE: trigger bar + FULL overlay (same as home)
  const [storeOverlayOpen, setStoreOverlayOpen] = useState(false);
  const [storeOverlaySearch, setStoreOverlaySearch] = useState("");
  const storeOverlayInputRef = useRef(null);

  // ===== App (small devices) search overlay state (single product only) =====
  const [appSearch, setAppSearch] = useState("");
  const [appSearchOpen, setAppSearchOpen] = useState(false);
  const appInputRef = useRef(null);

  // ✅ single product more-menu
  const [appMoreOpen, setAppMoreOpen] = useState(false);
  const appMoreWrapRef = useRef(null);

  const typeaheadRef = useRef(null);
  const catRef = useRef(null);

  // ===== Recent searches =====
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  const saveRecent = (term) => {
    const t = (term || "").trim();
    if (!t) return;

    setRecentSearches((prev) => {
      const next = [
        t,
        ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase()),
      ].slice(0, 10);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  };

 
  // ===== Dropdown options (AliExpress style) for DESKTOP =====
const dropdownOptions = useMemo(
  () =>
    buildSearchSuggestions({
      searchValue: searchTermState,
      recentSearches,
      productList,
      historyLimit: 8,
      productLimit: searchTermState.trim() ? 10 : 8,
      historyKeyPrefix: "dh",
      productKeyPrefix: "dp",
      includeProductsWhenEmpty: true,
    }),
  [recentSearches, productList, searchTermState]
);

/* =========================
   ✅ HOME OVERLAY suggestions
========================== */
const homeOverlaySuggestions = useMemo(
  () =>
    buildSearchSuggestions({
      searchValue: homeOverlaySearch,
      recentSearches,
      productList,
      historyLimit: 12,
      productLimit: 20,
      historyKeyPrefix: "hh",
      productKeyPrefix: "hp",
    }),
  [homeOverlaySearch, recentSearches, productList]
);

/* =========================
   ✅ STORE OVERLAY suggestions
========================== */
const storeOverlaySuggestions = useMemo(
  () =>
    buildSearchSuggestions({
      searchValue: storeOverlaySearch,
      recentSearches,
      productList,
      historyLimit: 12,
      productLimit: 20,
      historyKeyPrefix: "sh",
      productKeyPrefix: "sp",
    }),
  [storeOverlaySearch, recentSearches, productList]
);

/* =========================
   ✅ APP OVERLAY suggestions
========================== */
const appSuggestions = useMemo(
  () =>
    buildSearchSuggestions({
      searchValue: appSearch,
      recentSearches,
      productList,
      historyLimit: 8,
      productLimit: 12,
      historyKeyPrefix: "ah",
      productKeyPrefix: "ap",
    }),
  [appSearch, recentSearches, productList]
);



  // ===== Load products/categories/cart =====
  useEffect(() => {
    if (productList.length === 0)
      dispatch(getAllProducts({ page: 1, limit: 1000 }));
  }, [dispatch, productList.length]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!(backendCartList.length > 0)) dispatch(getUserCart());
  }, [dispatch, isLoggedIn, backendCartList.length]);

  // ===== Sticky header =====
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ===== Close category panel on outside click =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (catRef.current && !catRef.current.contains(event.target))
        setCatOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ close single-product more menu on outside click / route change
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        appMoreWrapRef.current &&
        !appMoreWrapRef.current.contains(event.target)
      ) {
        setAppMoreOpen(false);
      }
    };

    if (appMoreOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [appMoreOpen]);

  useEffect(() => {
    setAppMoreOpen(false);
  }, [location.pathname, location.search]);

  /* =========================
     ✅ Cart total qty (backend OR guest)
  ========================== */
  const cartQty = useMemo(() => {
    if (isLoggedIn) {
      return (backendCartList || []).reduce((acc, item) => {
        const q = item?.qty ?? item?.quantity ?? 0;
        return acc + Number(q || 0);
      }, 0);
    }

    return (guestCart || []).reduce((acc, item) => {
      const q = item?.quantity ?? item?.qty ?? 0;
      return acc + Number(q || 0);
    }, 0);
  }, [isLoggedIn, backendCartList, guestCart]);

  // ===== Search =====
  const runSearch = (term) => {
    const t = (term || searchTermState || "").trim();
    if (!t) return;

    saveRecent(t);
    pushSearchHistory(t);
    setSearchTermState(t);

    navigate(`/product?search=${encodeURIComponent(t)}`);

    setSearchOpen(false);
  };

  const runStoreSearch = (term) => {
    const t = (term || "").trim();
    if (!t) return;

    saveRecent(t);
    pushSearchHistory(t);
    setSearchTermState(t);

    const params = new URLSearchParams(location.search);
    const catId = (params.get("category") || "").trim();

    const next = new URLSearchParams();
    if (catId) next.set("category", catId);
    next.set("search", t);

    navigate(`/product?${next.toString()}`);

    setSearchOpen(false);
  };

  const goProductDetails = (item) => {
  const routeId =
    item?.routeId || getProductRouteId(item?.prod) || getProductRouteId(item);

  if (!routeId) return;

  navigate(`/product/${encodeURIComponent(routeId)}`);

  setSearchOpen(false);
  setAppSearchOpen(false);
  setHomeOverlayOpen(false);
  setStoreOverlayOpen(false);

  setAppSearch("");
  setHomeOverlaySearch("");
  setStoreOverlaySearch("");
};

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(searchTermState);
  };

  // ✅ Sync inputs with URL
  useEffect(() => {
    if (location.pathname !== "/product") return;

    const params = new URLSearchParams(location.search);
    const q = (params.get("search") || "").trim();

    setSearchTermState(q);
    setAppSearch(q);
    setStoreOverlaySearch(q);
  }, [location.pathname, location.search]);

  // ✅ CLEAR search input when navigating away from /product
  useEffect(() => {
    if (location.pathname === "/product") return;

    setSearchTermState("");
    setSearchOpen(false);
    typeaheadRef.current?.clear?.();

    setStoreOverlaySearch("");
    setHomeOverlaySearch("");
    setAppSearch("");
  }, [location.pathname]);

  /* =========================
     ✅ Mobile fixed bars -> body padding helpers
  ========================== */
  useEffect(() => {
    const clsSingle = "ku-appHeaderPad";
    if (isSingleProductPage) document.body.classList.add(clsSingle);
    else document.body.classList.remove(clsSingle);

    return () => document.body.classList.remove(clsSingle);
  }, [isSingleProductPage]);

  useEffect(() => {
    const clsHome = "ku-homeHeaderPad";
    if (isHomePage && !homeOverlayOpen) document.body.classList.add(clsHome);
    else document.body.classList.remove(clsHome);

    return () => document.body.classList.remove(clsHome);
  }, [isHomePage, homeOverlayOpen]);

  useEffect(() => {
    const clsStore = "ku-storeHeaderPad";
    if (isStorePage && !storeOverlayOpen)
      document.body.classList.add(clsStore);
    else document.body.classList.remove(clsStore);

    return () => document.body.classList.remove(clsStore);
  }, [isStorePage, storeOverlayOpen]);

  useEffect(() => {
    if (!isSingleProductPage && appSearchOpen) setAppSearchOpen(false);
  }, [isSingleProductPage, appSearchOpen]);

  useEffect(() => {
    if (!isStorePage && storeOverlayOpen) setStoreOverlayOpen(false);
  }, [isStorePage, storeOverlayOpen]);

  /* =========================
     ✅ HOME overlay open/close
  ========================== */
  useEffect(() => {
    if (!homeOverlayOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setTimeout(() => homeOverlayInputRef.current?.focus?.(), 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") setHomeOverlayOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [homeOverlayOpen]);

  const openHomeOverlay = () => {
    if (!isHomePage) return;
    setHomeOverlayOpen(true);
  };

  const closeHomeOverlay = () => {
    setHomeOverlayOpen(false);
  };

  const handleHomeOverlaySubmit = (e) => {
    e.preventDefault();
    const t = (homeOverlaySearch || "").trim();
    if (!t) return;
    runSearch(t);
    setHomeOverlayOpen(false);
    setHomeOverlaySearch("");
  };



  const pickHomeOverlaySuggestion = (item) => {
  if (!item) return;

  if (item.type === "product") {
    goProductDetails(item);
    return;
  }

  runSearch(item.label);
  setHomeOverlayOpen(false);
  setHomeOverlaySearch("");
};

  /* =========================
     ✅ STORE overlay open/close
  ========================== */
  useEffect(() => {
    if (!storeOverlayOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setTimeout(() => storeOverlayInputRef.current?.focus?.(), 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") setStoreOverlayOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [storeOverlayOpen]);

  const openStoreOverlay = () => {
    if (!isStorePage || isSingleProductPage) return;
    setStoreOverlayOpen(true);
  };

  const closeStoreOverlay = () => setStoreOverlayOpen(false);

  const handleStoreOverlaySubmit = (e) => {
    e.preventDefault();
    const t = (storeOverlaySearch || "").trim();
    if (!t) return;
    runStoreSearch(t);
    setStoreOverlayOpen(false);
  };

  
  const pickStoreOverlaySuggestion = (item) => {
  if (!item) return;

  if (item.type === "product") {
    goProductDetails(item);
    return;
  }

  runStoreSearch(item.label);
  setStoreOverlayOpen(false);
};

  // ✅ App overlay open/close
  useEffect(() => {
    if (!appSearchOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setTimeout(() => appInputRef.current?.focus?.(), 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") setAppSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [appSearchOpen]);

  const handleAppSearchSubmit = (e) => {
    e.preventDefault();
    const t = (appSearch || "").trim();
    if (!t) return;
    runSearch(t);
    setAppSearchOpen(false);
  };

  const openAppSearch = () => {
    if (!isSingleProductPage) return;
    setAppSearchOpen(true);
    setAppMoreOpen(false);
  };

  const closeAppSearch = () => setAppSearchOpen(false);

  const pickHistory = (term) => {
    runSearch(term);
    setAppSearchOpen(false);
    setAppSearch("");
  };

  const pickAppSuggestion = (item) => {
  if (!item) return;

  if (item.type === "product") {
    goProductDetails(item);
    return;
  }

  if (item.type === "history") {
    pickHistory(item.label);
    return;
  }

  runSearch(item.label);
  setAppSearchOpen(false);
  setAppSearch("");
};

  /* =========================
     ✅ AUTH NAV HELPERS
  ========================== */
  const goIdentification = (e, opts = {}) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    navigate("/identification", { state: { returnTo, ...opts } });
  };



  const goIdentify = (e) => {
  e?.preventDefault?.();
  if (isLoggedIn) return navigate("/my-profile");
  goIdentification(e);
};

  const goLogin = (e) => {
    e?.preventDefault?.();
    goIdentification(e);
  };

  // ✅ logout + full reload
  const handleLogout = () => {
    const preserveLocal = [GUEST_CART_KEY, RECENT_KEY];

    const keepLocal = {};
    preserveLocal.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) keepLocal[k] = v;
    });

    try {
      localStorage.clear();
      sessionStorage.clear();

      Object.entries(keepLocal).forEach(([k, v]) => {
        localStorage.setItem(k, v);
      });

      window.dispatchEvent(new Event("authUpdated"));
    } catch {
      // no-op
    }

    // ✅ full app reset
    window.location.replace("/");
  };

  // ===== Nav links =====
  const navLinks = useMemo(
    () => [
      { to: "/", label: "Home", requiresAuth: false },
      { to: "/product", label: "Our Store", requiresAuth: false },
      { to: "/my-orders", label: "My Orders", requiresAuth: true },
      { to: "/print", label: "Print Now", requiresAuth: false },
      { to: "/contact", label: "Contact", requiresAuth: false },
    ],
    []
  );

  const renderCats = (categories?.length ? categories : uniqueCategories) ?? [];

  const goCategory = (cat) => {
    const id = cat?._id || cat?.id;
    if (!id) return;

    setCatOpen(false);
    navigate(`/product?category=${encodeURIComponent(String(id))}`);
  };

  /* =========================
     ✅ IMPORTANT:
     Cart should ALWAYS go to /cart
  ========================== */
  const goCart = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    navigate("/cart");
    setAppMoreOpen(false);
  };

  // ✅ Wishlist requires login -> redirect to /identification
  const goWishlist = (e) => {
    if (isLoggedIn) {
      navigate("/whishlist");
      setAppMoreOpen(false);
      return;
    }
    goIdentification(e);
    setAppMoreOpen(false);
  };

  // ✅ My Orders requires login -> redirect to /identification
  const goProtectedNav = (e, item) => {
    if (!item?.requiresAuth) return;
    if (isLoggedIn) return;
    goIdentification(e);
  };

  // ✅ single product header back button
  const goAppBack = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (appSearchOpen) setAppSearchOpen(false);
    if (appMoreOpen) setAppMoreOpen(false);
    navigate(-1);
  };

  // ✅ single product more menu toggle
  const onAppMore = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAppMoreOpen((prev) => !prev);
  };

  const goAppHome = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAppMoreOpen(false);
    navigate("/");
  };

  const goAppCategories = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAppMoreOpen(false);
    navigate("/product");
  };

  const goAppCart = (e) => {
    goCart(e);
  };

  const goAppWishlist = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isLoggedIn) {
      setAppMoreOpen(false);
      navigate("/whishlist");
      return;
    }
    setAppMoreOpen(false);
    goIdentification(e);
  };

  const goAppAccount = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAppMoreOpen(false);
    if (isLoggedIn) {
      navigate("/my-profile");
      return;
    }
    goIdentification(e);
  };

  const appMoreLinks = [
    {
      key: "home",
      label: "Home",
      icon: <GoHome size={24} />,
      onClick: goAppHome,
    },
    {
      key: "categories",
      label: "Categories",
      icon: <TbCategory2 size={24} />,
      onClick: goAppCategories,
    },
    {
      key: "cart",
      label: "Cart",
      icon: <HiOutlineShoppingCart size={24} />,
      onClick: goAppCart,
    },
    {
      key: "wishlist",
      label: "Wishlist",
      icon: <IoHeartOutline size={24} />,
      onClick: goAppWishlist,
    },
    {
      key: "account",
      label: "Account",
      icon: <IoPersonOutline size={24} />,
      onClick: goAppAccount,
    },
  ];

  return (
    <header className={`rb-header ${isScrolled ? "scrolled" : ""}`}>
      {/* ✅ HOME PAGE MOBILE SEARCH BAR (TRIGGER ONLY) */}
      {isHomePage && (
        <div className="appHomeH-wrap">
          <header className="appHomeH" aria-label="Mobile search header">
            <button
              type="button"
              className="appHomeSearchBtn"
              onClick={openHomeOverlay}
              aria-label="Open search"
            >
              <span className="appHomeSearchIcon" aria-hidden="true">
                <BsSearch size={18} />
              </span>
              <span className="appHomeSearchPlaceholder">Search on Kupto</span>
            </button>
          </header>
        </div>
      )}

      {/* ✅ STORE PAGE MOBILE SEARCH BAR (TRIGGER ONLY) */}
      {isStorePage && !isSingleProductPage && (
        <div className="appHomeH-wrap">
          <header className="appHomeH" aria-label="Mobile search header">
            <button
              type="button"
              className="appHomeSearchBtn"
              onClick={openStoreOverlay}
              aria-label="Open search"
            >
              <span className="appHomeSearchIcon" aria-hidden="true">
                <BsSearch size={18} />
              </span>
              <span className="appHomeSearchPlaceholder">Search on Kupto</span>
            </button>
          </header>
        </div>
      )}

      {/* ✅ HOME FULLSCREEN SEARCH */}
      {isHomePage && homeOverlayOpen && (
        <div
          className="appSearchOverlay appSearchOverlay--home"
          role="dialog"
          aria-modal="true"
        >
          <div className="appSearchTop appSearchTop--jumia">
            <button
              type="button"
              className="appSearchBack appSearchBack--jumia"
              onClick={closeHomeOverlay}
              aria-label="Back"
            >
              <IoArrowBack size={22} />
            </button>

            <form
              className="appSearchBar appSearchBar--jumia"
              onSubmit={handleHomeOverlaySubmit}
            >
              <span className="appSearchIcon" aria-hidden="true">
                <BsSearch size={16} />
              </span>

              <input
                ref={homeOverlayInputRef}
                className="appSearchInput appSearchInput--jumia"
                value={homeOverlaySearch}
                onChange={(e) => setHomeOverlaySearch(e.target.value)}
                placeholder="Search on Kupto"
                type="search"
                autoComplete="off"
              />
            </form>

            <div className="appSearchActions">
              <button
                type="button"
                className="appSearchIconBtn"
                onClick={goCart}
                aria-label="Cart"
              >
                <FiShoppingCart size={22} />
                {cartQty > 0 ? (
                  <span
                    className="appSearchCartBadge"
                    aria-label={`${cartQty} items`}
                  >
                    {cartQty > 99 ? "99+" : cartQty}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                className="appSearchIconBtn"
                onClick={onAppMore}
                aria-label="More"
              >
                <BsThreeDotsVertical size={20} />
              </button>
            </div>
          </div>

          <div className="appSearchList">
            {homeOverlaySuggestions.length ? (
              homeOverlaySuggestions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="appSearchItem"
                  onClick={() => pickHomeOverlaySuggestion(item)}
                >
                  <span className="appSearchClock" aria-hidden="true">
                    {item.type === "history" ? (
                      <MdHistory size={18} />
                    ) : (
                      <BsSearch size={16} />
                    )}
                  </span>
                  <span className="appSearchText">{item.label}</span>
                </button>
              ))
            ) : (
              <div className="appSearchEmpty">
                {homeOverlaySearch.trim() ? "No results" : "No recent searches"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ STORE FULLSCREEN SEARCH */}
      {isStorePage && !isSingleProductPage && storeOverlayOpen && (
        <div
          className="appSearchOverlay appSearchOverlay--store"
          role="dialog"
          aria-modal="true"
        >
          <div className="appSearchTop appSearchTop--jumia">
            <button
              type="button"
              className="appSearchBack appSearchBack--jumia"
              onClick={closeStoreOverlay}
              aria-label="Back"
            >
              <IoArrowBack size={22} />
            </button>

            <form
              className="appSearchBar appSearchBar--jumia"
              onSubmit={handleStoreOverlaySubmit}
            >
              <span className="appSearchIcon" aria-hidden="true">
                <BsSearch size={16} />
              </span>

              <input
                ref={storeOverlayInputRef}
                className="appSearchInput appSearchInput--jumia"
                value={storeOverlaySearch}
                onChange={(e) => setStoreOverlaySearch(e.target.value)}
                placeholder="Search on Kupto"
                type="search"
                autoComplete="off"
              />
            </form>

            <div className="appSearchActions">
              <button
                type="button"
                className="appSearchIconBtn"
                onClick={goCart}
                aria-label="Cart"
              >
                <FiShoppingCart size={22} />
                {cartQty > 0 ? (
                  <span
                    className="appSearchCartBadge"
                    aria-label={`${cartQty} items`}
                  >
                    {cartQty > 99 ? "99+" : cartQty}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                className="appSearchIconBtn"
                onClick={onAppMore}
                aria-label="More"
              >
                <BsThreeDotsVertical size={20} />
              </button>
            </div>
          </div>

          <div className="appSearchList">
            {storeOverlaySuggestions.length ? (
              storeOverlaySuggestions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="appSearchItem"
                  onClick={() => pickStoreOverlaySuggestion(item)}
                >
                  <span className="appSearchClock" aria-hidden="true">
                    {item.type === "history" ? (
                      <MdHistory size={18} />
                    ) : (
                      <BsSearch size={16} />
                    )}
                  </span>
                  <span className="appSearchText">{item.label}</span>
                </button>
              ))
            ) : (
              <div className="appSearchEmpty">
                {storeOverlaySearch.trim()
                  ? "No results"
                  : "No recent searches"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <div className="rb-topbar">
        <div className="rb-container rb-topbar-row">
          <span className="rb-top-msg">Call to Order +256 200 949 142</span>
          <div className="rb-top-right">
            <Link to="/price-list" className="rb-top-link">
              Digital Printing Prices
            </Link>
            <Link to="/help" className="rb-top-link">
              Help
            </Link>
          </div>
        </div>
      </div>

      {/* MAIN BAR (DESKTOP/TABLET) */}
      <div className={`rb-mainbar ${isScrolled ? "rb-mainbar--stuck" : ""}`}>
        <div className="rb-container rb-main-row">
          <div className="rb-logo-wrap">
            <Link to="/">
              <img src={kuptoLogo} alt="Kupto logo" className="rb-logo" />
            </Link>
          </div>

          {/* DESKTOP SEARCH */}
          <form
            className="rb-search rb-search--openable"
            onSubmit={handleSearchSubmit}
          >
            <Typeahead
                ref={typeaheadRef}
                id="rb-search-products"
                options={dropdownOptions}
                labelKey="name"
                minLength={0}
                open={searchOpen && dropdownOptions.length > 0}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 120)}
                onChange={(selected) => {
                  if (!selected?.length) return;

                  const item = selected[0];

                  if (item.type === "product") {
                    goProductDetails(item);
                    return;
                  }

                  runSearch(item?.name);
                }}
                onInputChange={(text) => {
                  setSearchTermState(text);
                  setSearchOpen(true);
                }}
                placeholder="Search on Kupto…"
                className="rb-typeahead"
                inputProps={{
                  className: "rb-search-input",
                  value: searchTermState,
                  onKeyDownCapture: (e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    e.stopPropagation();
                    runSearch(searchTermState);
                  },
                }}
                renderMenu={(results, menuProps) => {
                  if (!results?.length) return null;

    return (
      <Menu {...menuProps} className="rb-searchMenu">
        {results.map((opt, idx) => (
          <MenuItem
            option={opt}
            position={idx}
            key={opt.id || idx}
            className="rb-searchItem"
          >
            <span className="rb-searchLeftIcon" aria-hidden="true">
              {opt.type === "history" ? (
                <MdHistory size={18} />
              ) : (
                <BsSearch size={16} />
              )}
            </span>

            <span className="rb-searchItemText">
              {opt.name}
              {opt.type === "history" && (
                <span className="rb-searchTag">Recent</span>
              )}
            </span>

            <span className="rb-searchRightIcon">↩</span>
          </MenuItem>
        ))}
      </Menu>
    );
  }}
/>

            <button className="rb-search-btn" type="submit" aria-label="Search">
              <BsSearch size={17} />
            </button>
          </form>

          <div className="rb-main-actions">
            <Link
              to={isLoggedIn ? "/my-profile" : "/identification"}
              onClick={!isLoggedIn ? goIdentify : undefined}
              state={!isLoggedIn ? { returnTo } : undefined}
              className="rb-user-greet rb-main-action"
            >
              <img src={userIcon} alt="" className="rb-greet-icon" />
                <span className="rb-greet-text">
                  {isLoggedIn
                    ? `Hello ${
                        [currentUser?.firstname, currentUser?.name]
                          .map((v) => String(v || "").trim())
                          .find((v) => v && v.toLowerCase() !== "user") || "Account"
                      }`
                    : "My Account"}
                </span>
            </Link>

            <Link
              to={isLoggedIn ? "/whishlist" : "/identification"}
              className="rb-main-action"
              onClick={goWishlist}
              state={!isLoggedIn ? { returnTo } : undefined}
            >
              <span className="rb-main-action-icon">
                <img src={heartIcon} alt="Wishlist" />
              </span>
              <span className="rb-icon-label">Wishlist</span>
            </Link>

            <Link to="/cart" className="rb-main-action" onClick={goCart}>
              <span className="rb-main-action-icon">
                <img src={cartIcon} alt="Cart" />
              </span>
              <span className="rb-icon-label">{cartQty}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* NAVBAR */}
      <div className="rb-navbar">
        <div className="rb-container rb-nav-row">
          {isHomePage && (
            <div className="rb-browse-wrap" ref={catRef}>
              <button
                className="rb-browse-btn"
                type="button"
                onClick={() => setCatOpen((p) => !p)}
                aria-expanded={catOpen}
              >
                <img src={menuIcon} alt="" className="rb-menu-icon" />
                <span>OUR STORE</span>
                <span className="rb-browse-caret">▾</span>
              </button>

              <div className={`rb-cat-panel ${catOpen ? "open" : ""}`}>
                {categoriesLoading ? (
                  <div className="rb-cat-skelWrap">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="rb-cat-skel" />
                    ))}
                  </div>
                ) : (
                  renderCats.map((cat) => {
                    const id = cat?._id || cat?.id;
                    const title = cat?.title || cat?.name || "Untitled";

                    return (
                      <button
                        key={id || title}
                        className="rb-cat-item"
                        type="button"
                        onClick={() => goCategory(cat)}
                      >
                        {title}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <nav className="rb-nav-menu">
            {navLinks.map((item) => {
              const to =
                item.requiresAuth && !isLoggedIn ? "/identification" : item.to;

              return (
                <NavLink
                  key={item.to}
                  to={to}
                  state={
                    item.requiresAuth && !isLoggedIn ? { returnTo } : undefined
                  }
                  onClick={(e) => goProtectedNav(e, item)}
                  className={({ isActive }) =>
                    `rb-nav-link ${isActive ? "active" : ""}`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className="rb-nav-link rb-logout-btn"
                type="button"
              >
                LOGOUT
              </button>
            ) : (
              <NavLink
                to="/identification"
                className="rb-nav-link"
                state={{ returnTo }}
                onClick={goLogin}
              >
                LOGIN
              </NavLink>
            )}
          </nav>
        </div>
      </div>

      {/* ✅ SINGLE PRODUCT MOBILE HEADER */}
      {isSingleProductPage && (
        <div className="appH-wrap">
          <header className="appH appH--jumia">
            <div className="appH-row appH-row--jumia">
              {/* left: back */}
              <button
                type="button"
                className="appH-iconBtn appH-backBtn"
                onClick={goAppBack}
                aria-label="Back"
              >
                <IoArrowBack size={22} />
              </button>

              {/* middle: search trigger */}
              <button
                type="button"
                className="appH-search appH-search--trigger appH-search--jumia"
                onClick={openAppSearch}
                aria-label="Search"
              >
                <span className="appH-searchIcon" aria-hidden="true">
                  <BsSearch size={16} />
                </span>
                <span className="appH-placeholder">Search on Kupto</span>
              </button>

              {/* right: cart + more */}
              <div className="appH-actions" ref={appMoreWrapRef}>
                <button
                  type="button"
                  className="appH-iconBtn"
                  onClick={goCart}
                  aria-label="Cart"
                >
                  <FiShoppingCart size={22} />
                  {cartQty > 0 ? (
                    <span
                      className="appH-cartBadge"
                      aria-label={`${cartQty} items`}
                    >
                      {cartQty > 99 ? "99+" : cartQty}
                    </span>
                  ) : null}
                </button>

                <button
                  type="button"
                  className="appH-iconBtn"
                  onClick={onAppMore}
                  aria-label="More"
                  aria-haspopup="menu"
                  aria-expanded={appMoreOpen ? "true" : "false"}
                >
                  <BsThreeDotsVertical size={20} />
                </button>

                {appMoreOpen && (
                  <div
                    className="appH-moreMenu"
                    role="menu"
                    aria-label="Quick links"
                  >
                    {appMoreLinks.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className="appH-moreItem"
                        role="menuitem"
                        onClick={item.onClick}
                      >
                        <span className="appH-moreIcon" aria-hidden="true">
                          {item.icon}
                        </span>
                        <span className="appH-moreText">{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>
      )}

      {/* ✅ FULLSCREEN APP SEARCH */}
      {isSingleProductPage && appSearchOpen && (
        <div className="appSearchOverlay" role="dialog" aria-modal="true">
          <div className="appSearchTop appSearchTop--jumia">
            <button
              type="button"
              className="appSearchBack appSearchBack--jumia"
              onClick={closeAppSearch}
              aria-label="Back"
            >
              <IoArrowBack size={22} />
            </button>

            <form
              className="appSearchBar appSearchBar--jumia"
              onSubmit={handleAppSearchSubmit}
            >
              <span className="appSearchIcon" aria-hidden="true">
                <BsSearch size={16} />
              </span>

              <input
                ref={appInputRef}
                className="appSearchInput appSearchInput--jumia"
                value={appSearch}
                onChange={(e) => setAppSearch(e.target.value)}
                placeholder="Search on Kupto"
                type="search"
                autoComplete="off"
              />
            </form>

            <div className="appSearchActions">
              <button
                type="button"
                className="appSearchIconBtn"
                onClick={goCart}
                aria-label="Cart"
              >
                <FiShoppingCart size={22} />
                {cartQty > 0 ? (
                  <span
                    className="appSearchCartBadge"
                    aria-label={`${cartQty} items`}
                  >
                    {cartQty > 99 ? "99+" : cartQty}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                className="appSearchIconBtn"
                onClick={onAppMore}
                aria-label="More"
              >
                <BsThreeDotsVertical size={20} />
              </button>
            </div>
          </div>

          <div className="appSearchList">
            {appSuggestions.length ? (
              appSuggestions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="appSearchItem"
                  onClick={() => pickAppSuggestion(item)}
                >
                  <span className="appSearchClock" aria-hidden="true">
                    {item.type === "history" ? (
                      <MdHistory size={18} />
                    ) : (
                      <BsSearch size={16} />
                    )}
                  </span>
                  <span className="appSearchText">{item.label}</span>
                </button>
              ))
            ) : (
              <div className="appSearchEmpty">
                {appSearch.trim() ? "No results" : "No recent searches"}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;